<script setup>
import { orderBy } from 'lodash-es';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  data: {
    type: Object,
    required: true,
  },
  config: {
    type: Object,
    required: true,
  },
  isPreview: {
    type: Boolean,
    required: true,
  },
});

const state = reactive({
  current_row: 0,
  collapsedGroups: new Set(),
});

const current_row_data = computed(() => props.data[state.current_row] || {});

const organized_data = computed(() => {
  const { columns_map = {}, column_groups = [] } = props.config.chart;
  const current_data = current_row_data.value;

  const all_items = orderBy([...Object.values(columns_map), ...column_groups], 'order_index');

  const result = [];
  const processed_fields = new Set();
  let current_field_collection = [];

  for (const item of all_items) {
    if (item.type === 'group') {
      if (current_field_collection.length > 0) {
        result.push({
          type: 'field_collection',
          key: `field_collection_${result.length}`,
          fields: current_field_collection,
        });
        current_field_collection = [];
      }

      const group_fields = item.columns
        .map(key => ({
          key,
          value: current_data[key],
          label: columns_map[key]?.label || key,
          visible: columns_map[key]?.visible,
        }))
        .filter(field => field.visible);

      if (group_fields.length > 0) {
        result.push({
          type: 'group',
          key: item.key,
          label: item.label,
          fields: group_fields,
        });
      }

      item.columns.forEach(key => processed_fields.add(key));
    }
    else if (!processed_fields.has(item.key) && item.visible && Object.prototype.hasOwnProperty.call(current_data, item.key)) {
      current_field_collection.push({
        key: item.key,
        value: current_data[item.key],
        label: item.label || item.key,
      });
      processed_fields.add(item.key);
    }
  }

  if (current_field_collection.length > 0) {
    result.push({
      type: 'field_collection',
      key: `field_collection_${result.length}`,
      fields: current_field_collection,
    });
  }

  return result;
});

function toggleGroupCollapse(groupKey) {
  if (state.collapsedGroups.has(groupKey)) {
    state.collapsedGroups.delete(groupKey);
  }
  else {
    state.collapsedGroups.add(groupKey);
  }
}

function isGroupCollapsed(groupKey) {
  return state.collapsedGroups.has(groupKey);
}

function nextRow() {
  state.current_row = (state.current_row + 1) % props.data.length;
}

function previousRow() {
  state.current_row = (state.current_row - 1 + props.data.length) % props.data.length;
}
</script>

<template>
  <div v-if="props.data.length" class="bi-details-card-widget h-full scrollbar" :class="props.isPreview ? '' : '-mr-3 pr-3'">
    <div class="space-y-4">
      <template v-for="(item, index) in organized_data" :key="item.key">
        <div
          v-if="item.type === 'group' && (index === 0 || organized_data[index - 1].type !== 'group')"
          class="border-t border-gray-200"
        />

        <div v-if="item.type === 'group'" class="space-y-4 pb-4 border-b border-gray-200">
          <div>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-1">
                <div
                  class="cursor-pointer transition-transform duration-200"
                  :class="{ 'rotate-90': !isGroupCollapsed(item.key) }"
                  @click="toggleGroupCollapse(item.key)"
                >
                  <IconHawkChevronRight class="text-gray-500 w-4 h-4" />
                </div>
                <h3 class="text-sm font-semibold text-gray-600">
                  {{ item.label || 'Untitled Group' }}
                </h3>
              </div>
            </div>
          </div>

          <div
            v-if="!isGroupCollapsed(item.key)"
            class="ml-5 mt-2"
          >
            <div
              class="grid gap-4 sm:gap-6"
              style="grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));"
            >
              <div
                v-for="field in item.fields"
                :key="field.key"
                class="flex flex-col"
              >
                <span class="text-sm font-medium text-gray-900">
                  {{ field.label }}
                </span>
                <span class="text-xs font-normal text-gray-700 truncate">
                  {{ field.value || '-' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          v-else-if="item.type === 'field_collection'"
          class="pt-2"
        >
          <div
            class="grid gap-4 sm:gap-6"
            style="grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));"
          >
            <div
              v-for="field in item.fields"
              :key="field.key"
              class="flex flex-col"
            >
              <span class="text-sm font-medium text-gray-900">
                {{ field.label }}
              </span>
              <span class="text-xs font-normal text-gray-700 truncate">
                {{ field.value || '-' }}
              </span>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div
      class="flex justify-end mt-8 items-center gap-3 text-sm text-gray-600 select-none"
    >
      <IconHawkChevronLeft
        class="cursor-pointer hover:text-gray-900 transition bi-exportable-icon"
        @click="previousRow"
      />
      <span class="font-medium">
        {{ state.current_row + 1 }} / {{ props.data.length }}
      </span>
      <IconHawkChevronRight
        class="cursor-pointer hover:text-gray-900 transition bi-exportable-icon"
        @click="nextRow"
      />
    </div>
  </div>
  <div v-else class="h-full w-full flex flex-col justify-center items-center text-center">
    <div class="text-sm font-semibold text-gray-900 mt-4 mb-1">
      {{ $t('No data to display') }}
    </div>
  </div>
</template>
